﻿import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import {
  alterarCliente,
  alternarCliente,
  criarCliente,
  listarClientes,
} from '@/features/cadastros/api/cadastrosApi';
import { chaveDeReferencias } from '@/features/referencias/useReferencias';
import { descreverErro } from '@/lib/erros';
import type { ClienteNaLista } from '@/types/api';

import estilos from './Administracao.module.css';

/**
 * Cadastro de clientes.
 *
 * Somente o nome e editavel. `CodigoExterno` e `Cnpj` existem no modelo como reserva para
 * a integracao com o cadastro de clientes do FAP e ficam nulos na v1: abri-los aqui
 * criaria um cadastro paralelo ao do FAP, que e o que a reserva evita.
 *
 * Inativa, nunca apaga — acao e historico apontam para a linha.
 */

/** Espera antes de buscar enquanto o usuario digita. */
const ESPERA_DA_BUSCA_EM_MS = 400;

export function PaginaDeClientes() {
  const queryClient = useQueryClient();

  const [texto, definirTexto] = useState('');
  const [textoAplicado, definirTextoAplicado] = useState('');

  useEffect(() => {
    const temporizador = setTimeout(() => {
      definirTextoAplicado(texto);
    }, ESPERA_DA_BUSCA_EM_MS);

    return () => {
      clearTimeout(temporizador);
    };
  }, [texto]);

  const { data, error, isPending } = useQuery({
    queryKey: ['clientes', textoAplicado],
    queryFn: ({ signal }) => listarClientes(textoAplicado, signal),
    placeholderData: (anterior) => anterior,
  });

  const [emEdicao, definirEmEdicao] = useState<number | null>(null);
  const [nome, definirNome] = useState('');
  const [falha, definirFalha] = useState<string | null>(null);

  // Referencias tambem, senao o cliente novo nao aparece no seletor de nova acao.
  const recarregar = async () => {
    await queryClient.invalidateQueries({ queryKey: ['clientes'] });
    await queryClient.invalidateQueries({ queryKey: chaveDeReferencias });
  };

  const limpar = () => {
    definirEmEdicao(null);
    definirNome('');
  };

  const salvar = useMutation({
    mutationFn: (valor: string) =>
      emEdicao === null ? criarCliente(valor) : alterarCliente(emEdicao, valor),
    onSuccess: async () => {
      definirFalha(null);
      limpar();
      await recarregar();
    },
    onError: (erro) => {
      definirFalha(descreverErro(erro));
    },
  });

  const alternar = useMutation({
    mutationFn: ({ id, ativar }: { id: number; ativar: boolean }) => alternarCliente(id, ativar),
    onSuccess: async () => {
      definirFalha(null);
      await recarregar();
    },
    onError: (erro) => {
      definirFalha(descreverErro(erro));
    },
  });

  const inativar = (cliente: ClienteNaLista) => {
    const aviso =
      cliente.acoesVinculadas > 0
        ? `${String(cliente.acoesVinculadas)} ` +
          `${cliente.acoesVinculadas === 1 ? 'ação aponta' : 'ações apontam'} para ` +
          `"${cliente.nome}". Elas continuam funcionando, e o cliente deixa de aparecer ` +
          'em cadastro novo. Inativar?'
        : `Inativar o cliente "${cliente.nome}"?`;

    if (window.confirm(aviso)) {
      alternar.mutate({ id: cliente.id, ativar: false });
    }
  };

  const ocupado = salvar.isPending || alternar.isPending;

  return (
    <section className={estilos.pagina}>
      <div>
        <h2 className={estilos.titulo}>Clientes</h2>
        <p className={estilos.apoio}>
          Toda ação precisa de ao menos um cliente afetado. Código externo e CNPJ ficam reservados
          para a integracao com o cadastro do FAP.
        </p>
      </div>

      {falha !== null && (
        <p className={estilos.falha} role="alert">
          {falha}
        </p>
      )}

      <section className={estilos.cartao}>
        <h2 className={estilos.secao}>{emEdicao === null ? 'Novo cliente' : 'Editar cliente'}</h2>

        <form
          onSubmit={(evento) => {
            evento.preventDefault();
            salvar.mutate(nome);
          }}
        >
          <div className={estilos.linha}>
            <div className={estilos.campo}>
              <label className={estilos.rotulo} htmlFor="nomeDoCliente">
                Nome
              </label>
              <input
                id="nomeDoCliente"
                className={estilos.entrada}
                value={nome}
                required
                onChange={(evento) => {
                  definirNome(evento.target.value);
                }}
              />
            </div>
          </div>

          {/* Fora da grade dos campos. Dentro dela, a auto-colocacao punha o botao na
              primeira celula livre da faixa do controle — a coluna 1 — e empurrava o campo
              Nome para a coluna 2. Ver o comentario em Administracao.module.css. */}
          <div className={estilos.acoes}>
            {emEdicao !== null && (
              <button className={estilos.botaoSecundario} type="button" onClick={limpar}>
                Desistir
              </button>
            )}

            <button className={estilos.botaoPrimario} type="submit" disabled={ocupado}>
              {emEdicao === null ? 'Criar cliente' : 'Salvar'}
            </button>
          </div>
        </form>
      </section>

      <div className={estilos.linha}>
        <div className={estilos.campo}>
          <label className={estilos.rotulo} htmlFor="buscaDeCliente">
            Buscar
          </label>
          <input
            id="buscaDeCliente"
            className={estilos.entrada}
            type="search"
            placeholder="Nome do cliente"
            value={texto}
            onChange={(evento) => {
              definirTexto(evento.target.value);
            }}
          />
        </div>
      </div>

      <div className={estilos.moldura}>
        <table className={estilos.tabela}>
          <caption className="apenas-leitor-de-tela">Clientes cadastrados</caption>
          <thead>
            <tr>
              <th scope="col">Nome</th>
              <th scope="col">Ações vinculadas</th>
              <th scope="col">Situação</th>
              <th scope="col" />
            </tr>
          </thead>
          <tbody>
            {data?.map((cliente) => (
              <tr key={cliente.id}>
                <td>{cliente.nome}</td>
                <td>{String(cliente.acoesVinculadas)}</td>
                <td>
                  <span className={cliente.ativo ? estilos.seloAtivo : estilos.seloInativo}>
                    {cliente.ativo ? 'Ativo' : 'Inativo'}
                  </span>
                </td>
                <td>
                  <div className={estilos.acoesDaLinha}>
                    <button
                      className={estilos.botaoSecundario}
                      type="button"
                      disabled={ocupado}
                      onClick={() => {
                        definirEmEdicao(cliente.id);
                        definirNome(cliente.nome);
                      }}
                    >
                      Editar
                    </button>
                    {cliente.ativo ? (
                      <button
                        className={estilos.botaoAtencao}
                        type="button"
                        disabled={ocupado}
                        onClick={() => {
                          inativar(cliente);
                        }}
                      >
                        Inativar
                      </button>
                    ) : (
                      <button
                        className={estilos.botaoSecundario}
                        type="button"
                        disabled={ocupado}
                        onClick={() => {
                          alternar.mutate({ id: cliente.id, ativar: true });
                        }}
                      >
                        Reativar
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {isPending && <p className={estilos.aviso}>Carregando clientes...</p>}

        {error !== null && (
          <p className={estilos.aviso} role="alert">
            {descreverErro(error)}
          </p>
        )}

        {data !== undefined && data.length === 0 && (
          <p className={estilos.aviso}>
            {textoAplicado === ''
              ? 'Nenhum cliente cadastrado. Sem cliente não é possível criar ação (Decisão 11).'
              : 'Nenhum cliente com esse nome.'}
          </p>
        )}
      </div>
    </section>
  );
}
